import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function BelanjaTab() {
  const [options, setOptions] = useState({ locations: [], ingredients: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    location_id: "",
    staff_name: "",
    notes: ""
  });
  const [items, setItems] = useState([{ ingredient_id: "", qty: "1", unit_price: "0" }]);
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [validating, setValidating] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");

  const validateForm = async () => {
    setValidating(true);
    setValidationMessage("");
    try {
      const response = await api.post("/stock-in/belanja/validate", {
        date: form.date,
        location_id: form.location_id,
        staff_name: form.staff_name,
        notes: form.notes,
        supplier_id: supplierId || null,
        supplier_name: supplierName.trim() || null,
        payment_method: paymentMethod,
        items: items.map((item) => ({
          ingredient_id: item.ingredient_id,
          qty: Number(item.qty),
          unit_price: Number(item.unit_price)
        }))
      });
      setValidationMessage(`Validasi berhasil. ${response.data.item_count} baris bahan, total Rp ${Number(response.data.total).toLocaleString("id-ID")}. Belum disimpan.`);
    } catch (err) {
      setValidationMessage(err.response?.data?.detail || "Validasi gagal. Periksa lokasi dan data bahan baku.");
    } finally {
      setValidating(false);
    }
  };

  const updateItem = (index, field, value) =>
    setItems((prev) => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));

  const addItem = () =>
    setItems((prev) => [...prev, { ingredient_id: "", qty: "1", unit_price: "0" }]);

  const removeItem = (index) =>
    setItems((prev) => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);

  const total = items.reduce((sum, item) =>
    sum + Math.max(0, Number(item.qty) || 0) * Math.max(0, Number(item.unit_price) || 0), 0);

  const updateForm = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  useEffect(() => {
    let active = true;
    api.get("/stock-in/belanja/options")
      .then((r) => { if (active) setOptions(r.data); })
      .catch(() => { if (active) setError("Gagal memuat data formulir."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  return <div className="rounded-lg border bg-white p-6">
    <h2 className="text-lg font-semibold">Belanja Bahan Baku</h2>
    {loading && <p>Memuat data formulir...</p>}
    {error && <p className="text-red-600">{error}</p>}
    {!loading && !error && <>
      <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm font-medium">Tanggal transaksi
          <input type="date" className="w-full rounded-md border p-2" value={form.date} onChange={(e) => updateForm("date", e.target.value)} />
        </label>
        <label className="space-y-1 text-sm font-medium">Lokasi penerimaan
          <select className="w-full rounded-md border p-2" value={form.location_id} onChange={(e) => updateForm("location_id", e.target.value)}>
            <option value="">Pilih gudang atau outlet</option>
            {options.locations.map((loc) => <option key={`${loc.type}-${loc.id}`} value={loc.id}>{loc.name} ({loc.type === "warehouse" ? "Gudang" : "Outlet"})</option>)}
          </select>
        </label>
        <label className="space-y-1 text-sm font-medium">Nama petugas
          <input className="w-full rounded-md border p-2" value={form.staff_name} onChange={(e) => updateForm("staff_name", e.target.value)} placeholder="Nama penerima barang" />
        </label>
        <label className="space-y-1 text-sm font-medium">Catatan
          <textarea className="w-full rounded-md border p-2" value={form.notes} onChange={(e) => updateForm("notes", e.target.value)} placeholder="Catatan transaksi (opsional)" rows={2} />
        </label>
      </div>
      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold">Daftar bahan baku</h3>
          <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={addItem}>+ Tambah bahan</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[700px] text-sm">
            <thead><tr className="border-b text-left">
              <th className="p-2">Bahan baku</th><th className="p-2">Jumlah</th><th className="p-2">Harga satuan (Rp)</th><th className="p-2">Subtotal (Rp)</th><th className="p-2">Aksi</th>
            </tr></thead>
            <tbody>{items.map((item, index) => <tr key={index} className="border-b">
              <td className="p-2"><select className="w-full rounded-md border p-2" value={item.ingredient_id} onChange={(e) => updateItem(index, "ingredient_id", e.target.value)}>
                <option value="">Pilih bahan</option>
                {options.ingredients.map((ingredient) => <option key={ingredient.id} value={ingredient.id}>{ingredient.name}</option>)}
              </select></td>
              <td className="p-2"><input type="number" min="0.001" step="any" className="w-24 rounded-md border p-2" value={item.qty} onChange={(e) => updateItem(index, "qty", e.target.value)} /></td>
              <td className="p-2"><input type="number" min="0" step="1" className="w-32 rounded-md border p-2" value={item.unit_price} onChange={(e) => updateItem(index, "unit_price", e.target.value)} /></td>
              <td className="p-2 whitespace-nowrap">{(Math.max(0, Number(item.qty) || 0) * Math.max(0, Number(item.unit_price) || 0)).toLocaleString("id-ID")}</td>
              <td className="p-2"><button type="button" className="rounded-md border px-2 py-1 text-red-600" disabled={items.length === 1} onClick={() => removeItem(index)}>Hapus</button></td>
            </tr>)}</tbody>
          </table>
        </div>
        <div className="flex justify-end border-t pt-4 text-lg font-semibold">Total belanja: Rp {total.toLocaleString("id-ID")}</div>
      </div>
      <div className="mt-6 space-y-4 border-t pt-5">
        <h3 className="font-semibold">Supplier dan pembayaran</h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className="space-y-1 text-sm font-medium">Supplier terdaftar
            <select className="w-full rounded-md border p-2" value={supplierId} onChange={(e) => { setSupplierId(e.target.value); setSupplierName(""); }}>
              <option value="">Pilih supplier (opsional)</option>
              {options.suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-sm font-medium">Nama supplier baru (jika belum terdaftar)
            <input className="w-full rounded-md border p-2" value={supplierName} onChange={(e) => { setSupplierName(e.target.value); if (e.target.value) setSupplierId(""); }} placeholder="Masukkan nama supplier" />
          </label>
          <label className="space-y-1 text-sm font-medium">Metode pembayaran
            <select className="w-full rounded-md border p-2" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <option value="cash">Tunai</option>
              <option value="transfer">Transfer bank</option>
              <option value="credit">Utang supplier</option>
            </select>
          </label>
          <div className="rounded-md bg-gray-50 p-3">
            <p className="text-sm text-gray-500">Total transaksi</p>
            <p className="text-xl font-bold">Rp {total.toLocaleString("id-ID")}</p>
            <p className="text-xs text-gray-500">{paymentMethod === "credit" ? "Akan dicatat sebagai utang setelah integrasi backend selesai." : paymentMethod === "transfer" ? "Transfer bank — akun pembayaran akan ditentukan backend." : "Pembayaran tunai — akun kas akan ditentukan backend."}</p>
          </div>
        </div>
      </div>
      <div className="mt-6 border-t pt-4">
        <button type="button" disabled={validating} onClick={validateForm} className="rounded-md bg-black px-5 py-2 text-white disabled:opacity-50">
          {validating ? "Memvalidasi..." : "Validasi Formulir"}
        </button>
        {validationMessage && <p className="mt-3 text-sm" role="status">{validationMessage}</p>}
      </div>
      <p className="mt-4 text-sm text-gray-500">Data pilihan tersedia — Lokasi: {options.locations.length} · Bahan baku: {options.ingredients.length} · Supplier: {options.suppliers.length}</p>
    </>}
  </div>;
}


function TransferTab() {
  const [locations, setLocations] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [sourceKey, setSourceKey] = useState("");
  const [destKey, setDestKey] = useState("");
  const [transferDate, setTransferDate] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [received, setReceived] = useState({});
  const [conditions, setConditions] = useState({});
  const [notes, setNotes] = useState("");

  const locationKey = (type, id) => `${String(type || "").toLowerCase()}:${id || ""}`;

  const locationLabel = (type, id, fallback) => {
    const found = locations.find(
      (loc) => locationKey(loc.type, loc.id) === locationKey(type, id)
    );
    return found?.name || fallback || id || "-";
  };

  const loadData = async () => {
    setLoading(true);
    setError("");
    try {
      const [warehouseResponse, outletResponse, transferResponse] =
        await Promise.all([
          api.get("/warehouses"),
          api.get("/outlets"),
          api.get("/transfers"),
        ]);

      const warehouses = (Array.isArray(warehouseResponse.data)
        ? warehouseResponse.data : []).map((item) => ({
          id: item.id,
          name: item.name,
          type: "warehouse",
        }));

      const outlets = (Array.isArray(outletResponse.data)
        ? outletResponse.data : []).map((item) => ({
          id: item.id,
          name: item.name,
          type: "outlet",
        }));

      setLocations([...warehouses, ...outlets]);
      setTransfers(Array.isArray(transferResponse.data) ? transferResponse.data : []);
    } catch (err) {
      setError(err.response?.data?.detail || "Gagal memuat data transfer dan lokasi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const inTransit = transfers.filter((transfer) => {
    if (transfer.status !== "IN_TRANSIT") return false;

    const term = search.trim().toLowerCase();
    if (term && !String(transfer.transfer_no || transfer.id || "").toLowerCase().includes(term)) {
      return false;
    }

    if (sourceKey && locationKey(transfer.source_type, transfer.source_id) !== sourceKey) {
      return false;
    }

    if (destKey && locationKey(transfer.dest_type, transfer.dest_id) !== destKey) {
      return false;
    }

    if (transferDate) {
      const createdDate = String(transfer.created_at || "").slice(0, 10);
      if (createdDate !== transferDate) return false;
    }

    return true;
  });

  const selected = transfers.find((transfer) => transfer.id === selectedId) || null;

  const selectTransfer = (transfer) => {
    setSelectedId(transfer.id);
    setReceived(Object.fromEntries(
      (transfer.items || []).map((item) => [item.entity_id, String(item.qty ?? "")])
    ));
    setConditions(Object.fromEntries(
      (transfer.items || []).map((item) => [item.entity_id, "baik"])
    ));
    setNotes("");
    setMessage("");
    setError("");
  };

  const updateReceived = (entityId, value) => {
    setReceived((prev) => ({ ...prev, [entityId]: value }));
  };

  const updateCondition = (entityId, value) => {
    setConditions((prev) => ({ ...prev, [entityId]: value }));
  };

  const submitReceipt = async () => {
    if (!selected || saving) return;

    const items = selected.items || [];
    if (!items.length) {
      setError("Transfer ini tidak memiliki daftar barang.");
      return;
    }

    for (const item of items) {
      const value = received[item.entity_id];
      const qty = Number(value);
      const sent = Number(item.qty);

      if (value === "" || !Number.isFinite(qty) || qty < 0 || qty > sent) {
        setError(`Jumlah diterima untuk ${item.name || item.entity_id} harus antara 0 dan ${sent}.`);
        return;
      }
    }

    const conditionNotes = items.map((item) => {
      const qty = Number(received[item.entity_id]);
      const sent = Number(item.qty);
      const condition = conditions[item.entity_id] || "belum dipilih";
      const varianceReason = qty !== sent
        ? `; selisih: ${qty < sent ? "kurang" : "lebih"}`
        : "";
      return `${item.name || item.entity_id}: kondisi ${condition}${varianceReason}`;
    });

    const receiptNotes = [
      notes.trim(),
      "Kondisi barang saat diterima:",
      ...conditionNotes,
    ].filter(Boolean).join("\n");

    setSaving(true);
    setError("");
    setMessage("");

    try {
      await api.post(`/transfers/${selected.id}/receive`, {
        items: items.map((item) => ({
          entity_id: item.entity_id,
          received_qty: Number(received[item.entity_id]),
          variance_reason: Number(received[item.entity_id]) !== Number(item.qty)
            ? "Selisih jumlah saat penerimaan"
            : "",
        })),
        notes: receiptNotes,
      });

      setMessage(`Transfer ${selected.transfer_no || selected.id} berhasil diterima dan diproses.`);
      setSelectedId("");
      setReceived({});
      setConditions({});
      setNotes("");
      await loadData();
    } catch (err) {
      setError(err.response?.data?.detail || "Penerimaan gagal. Periksa status transfer lalu muat ulang data.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 rounded-lg border bg-white p-4 md:p-6">
      <div>
        <h2 className="text-lg font-semibold">Penerimaan Transfer</h2>
        <p className="mt-1 text-sm text-gray-500">
          Pilih transfer berstatus Dalam Perjalanan, periksa barang, lalu konfirmasi penerimaan.
          Penerimaan diproses satu kali untuk setiap transfer.
        </p>
      </div>

      {loading && <p className="text-sm text-gray-500">Memuat transfer dan lokasi...</p>}
      {error && <p role="alert" className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</p>}
      {message && <p role="status" className="rounded-md bg-green-50 p-3 text-sm text-green-700">{message}</p>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="space-y-1 text-sm font-medium">
          Tanggal transfer
          <input
            type="date"
            className="w-full rounded-md border p-2"
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
          />
        </label>

        <label className="space-y-1 text-sm font-medium">
          Cari nomor transfer
          <input
            className="w-full rounded-md border p-2"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Masukkan nomor transfer"
          />
        </label>

        <label className="space-y-1 text-sm font-medium">
          Lokasi sumber
          <select
            className="w-full rounded-md border p-2"
            value={sourceKey}
            onChange={(e) => { setSourceKey(e.target.value); setSelectedId(""); }}
          >
            <option value="">Semua lokasi sumber</option>
            {locations.map((loc) => (
              <option key={locationKey(loc.type, loc.id)} value={locationKey(loc.type, loc.id)}>
                {loc.name} ({loc.type === "warehouse" ? "Gudang" : "Outlet"})
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 text-sm font-medium">
          Lokasi tujuan
          <select
            className="w-full rounded-md border p-2"
            value={destKey}
            onChange={(e) => { setDestKey(e.target.value); setSelectedId(""); }}
          >
            <option value="">Semua lokasi tujuan</option>
            {locations.map((loc) => (
              <option key={locationKey(loc.type, loc.id)} value={locationKey(loc.type, loc.id)}>
                {loc.name} ({loc.type === "warehouse" ? "Gudang" : "Outlet"})
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-semibold">Transfer yang menunggu penerimaan</h3>
          <button type="button" className="rounded-md border px-3 py-2 text-sm" onClick={loadData} disabled={loading || saving}>
            Muat ulang
          </button>
        </div>

        {inTransit.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-gray-500">
            Tidak ada transfer Dalam Perjalanan yang cocok dengan filter.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-[680px] text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left">
                  <th className="p-3">Nomor</th>
                  <th className="p-3">Tanggal</th>
                  <th className="p-3">Dari</th>
                  <th className="p-3">Tujuan</th>
                  <th className="p-3">Barang</th>
                  <th className="p-3">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {inTransit.map((transfer) => (
                  <tr key={transfer.id} className="border-t">
                    <td className="p-3 font-medium">{transfer.transfer_no || transfer.id}</td>
                    <td className="p-3">{String(transfer.created_at || "").slice(0, 10) || "-"}</td>
                    <td className="p-3">{locationLabel(transfer.source_type, transfer.source_id, transfer.source_name)}</td>
                    <td className="p-3">{locationLabel(transfer.dest_type, transfer.dest_id, transfer.dest_name)}</td>
                    <td className="p-3">{(transfer.items || []).length} item</td>
                    <td className="p-3">
                      <button
                        type="button"
                        className="rounded-md bg-black px-3 py-2 text-white"
                        onClick={() => selectTransfer(transfer)}
                      >
                        {selectedId === transfer.id ? "Dipilih" : "Terima"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div className="space-y-4 border-t pt-5">
          <div>
            <h3 className="font-semibold">Rincian penerimaan: {selected.transfer_no || selected.id}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {locationLabel(selected.source_type, selected.source_id, selected.source_name)}
              {" → "}
              {locationLabel(selected.dest_type, selected.dest_id, selected.dest_name)}
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Jumlah yang diterima tidak boleh melebihi jumlah kirim. Jika kurang, penerimaan ini tetap menyelesaikan transfer dan mencatat selisih.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="p-2">Barang</th>
                  <th className="p-2">Jenis</th>
                  <th className="p-2">Dikirim</th>
                  <th className="p-2">Aktual diterima</th>
                  <th className="p-2">Kondisi</th>
                </tr>
              </thead>
              <tbody>
                {(selected.items || []).map((item) => (
                  <tr key={item.entity_id} className="border-b">
                    <td className="p-2">{item.name || item.entity_id}</td>
                    <td className="p-2">{item.entity === "ingredient" ? "Bahan baku" : "Produk"}</td>
                    <td className="p-2">{item.qty}</td>
                    <td className="p-2">
                      <input
                        type="number"
                        min="0"
                        max={Number(item.qty)}
                        step="any"
                        className="w-28 rounded-md border p-2"
                        value={received[item.entity_id] ?? ""}
                        onChange={(e) => updateReceived(item.entity_id, e.target.value)}
                      />
                    </td>
                    <td className="p-2">
                      <select
                        className="rounded-md border p-2"
                        value={conditions[item.entity_id] || "baik"}
                        onChange={(e) => updateCondition(item.entity_id, e.target.value)}
                      >
                        <option value="baik">Baik</option>
                        <option value="rusak">Rusak</option>
                        <option value="kurang">Kurang</option>
                        <option value="perlu pemeriksaan">Perlu pemeriksaan</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="block space-y-1 text-sm font-medium">
            Catatan penerimaan
            <textarea
              className="w-full rounded-md border p-2"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Contoh: kemasan basah, segel terbuka, atau catatan lainnya"
            />
          </label>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded-md bg-black px-5 py-2 text-white disabled:opacity-50"
              onClick={submitReceipt}
              disabled={saving || loading}
            >
              {saving ? "Memproses penerimaan..." : "Konfirmasi Penerimaan"}
            </button>
            <button
              type="button"
              className="rounded-md border px-5 py-2"
              disabled={saving}
              onClick={() => { setSelectedId(""); setError(""); }}
            >
              Batal
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ManualTab() {
  const { user } = useAuth();
  const [options, setOptions] = useState({ locations: [], ingredients: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [date, setDate] = useState(new Date().toLocaleDateString("en-CA"));
  const [locationId, setLocationId] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([{ kind: "ingredient", id: "", name: "", qty: "1", unit: "" }]);

  const staffName = user?.name || user?.full_name || user?.username || user?.email || "Pengguna login";

  useEffect(() => {
    let active = true;
    api.get("/stock-in/belanja/options")
      .then((r) => {
        if (active) setOptions({
          locations: Array.isArray(r.data?.locations) ? r.data.locations : [],
          ingredients: Array.isArray(r.data?.ingredients) ? r.data.ingredients : [],
          suppliers: Array.isArray(r.data?.suppliers) ? r.data.suppliers : []
        });
      })
      .catch(() => { if (active) setError("Gagal memuat data lokasi dan ingredient."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const ingredientFor = (id) => options.ingredients.find((x) => String(x.id || x._id) === String(id));
  const updateItem = (i, patch) => setItems((prev) => prev.map((x, n) => n === i ? { ...x, ...patch } : x));
  const addItem = (kind) => setItems((prev) => [...prev, { kind, id: "", name: "", qty: "1", unit: "" }]);
  const removeItem = (i) => setItems((prev) => prev.length > 1 ? prev.filter((_, n) => n !== i) : prev);

  return (
    <div className="space-y-5 rounded-lg border bg-white p-4 md:p-6">
      <div>
        <h2 className="text-lg font-semibold">Form Stock In Manual</h2>
        <p className="mt-1 text-sm text-slate-500">Pencatatan barang masuk secara manual.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <label className="text-sm font-medium">Tanggal
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="mt-1 block w-full rounded-md border px-3 py-2 font-normal" />
        </label>
        <label className="text-sm font-medium">Lokasi
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}
            disabled={loading || !!error}
            className="mt-1 block w-full rounded-md border bg-white px-3 py-2 font-normal">
            <option value="">Pilih lokasi</option>
            {options.locations.map((x) => {
              const id = String(x.id || x._id || "");
              return <option key={id} value={id}>{x.name || x.outlet_name || x.warehouse_name || id}</option>;
            })}
          </select>
        </label>
        <label className="text-sm font-medium">Nama Staf
          <input readOnly value={staffName}
            className="mt-1 block w-full rounded-md border bg-slate-50 px-3 py-2 font-normal" />
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => addItem("ingredient")}
          className="rounded-md border border-emerald-600 px-3 py-2 text-sm font-medium text-emerald-700">
          + Tambah Ingredient
        </button>
        <button type="button" onClick={() => addItem("product")}
          className="rounded-md border border-blue-600 px-3 py-2 text-sm font-medium text-blue-700">
          + Tambah Product
        </button>
      </div>

      <div className="space-y-3">
        {items.map((item, i) => {
          const ingredient = item.kind === "ingredient" ? ingredientFor(item.id) : null;
          return <div key={i} className="rounded-lg border p-3">
            <div className="mb-3 flex items-center justify-between">
              <strong className="text-sm">{item.kind === "ingredient" ? "Ingredient" : "Product"} {i + 1}</strong>
              <button type="button" disabled={items.length === 1} onClick={() => removeItem(i)}
                className="px-2 py-1 text-sm text-red-600 disabled:opacity-40">Hapus</button>
            </div>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {item.kind === "ingredient" ?
                <label className="text-sm font-medium">Nama Ingredient
                  <select value={item.id} onChange={(e) => {
                    const x = ingredientFor(e.target.value);
                    updateItem(i, { id: e.target.value, name: x?.name || "", unit: x?.unit || "" });
                  }} disabled={loading || !!error}
                    className="mt-1 block w-full rounded-md border bg-white px-3 py-2 font-normal">
                    <option value="">Pilih ingredient</option>
                    {options.ingredients.map((x) => {
                      const id = String(x.id || x._id || "");
                      return <option key={id} value={id}>{x.name || id}</option>;
                    })}
                  </select>
                </label> :
                <label className="text-sm font-medium">Nama Product
                  <input value={item.name} onChange={(e) => updateItem(i, { name: e.target.value, id: "", unit: "" })}
                    placeholder="Nama produk"
                    className="mt-1 block w-full rounded-md border px-3 py-2 font-normal" />
                </label>}
              <label className="text-sm font-medium">Kuantitas
                <input type="number" min="0.001" step="any" value={item.qty}
                  onChange={(e) => updateItem(i, { qty: e.target.value })}
                  className="mt-1 block w-full rounded-md border px-3 py-2 font-normal" />
              </label>
              <label className="text-sm font-medium">Satuan
                <input value={ingredient?.unit || item.unit}
                  onChange={(e) => updateItem(i, { unit: e.target.value })}
                  readOnly={!!ingredient?.unit} placeholder="Satuan"
                  className="mt-1 block w-full rounded-md border bg-slate-50 px-3 py-2 font-normal" />
              </label>
            </div>
            {item.kind === "product" && <p className="mt-2 text-xs text-amber-700">Pilihan produk master belum terhubung.</p>}
          </div>;
        })}
      </div>

      <label className="block text-sm font-medium">Catatan
        <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Tambahkan catatan jika diperlukan"
          className="mt-1 block w-full rounded-md border px-3 py-2 font-normal" />
      </label>

      {loading && <p className="text-sm text-slate-500">Memuat data formulir...</p>}
      {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
        Submit belum diaktifkan: endpoint penyimpanan manual dan pencatatan ledger perlu disiapkan terlebih dahulu.
      </div>
      <button type="button" disabled
        className="w-full cursor-not-allowed rounded-md bg-slate-300 px-4 py-2.5 font-semibold text-slate-600 md:w-auto">
        Submit Stock In
      </button>
    </div>
  );
}


function RiwayatTab() {
  const [options, setOptions] = useState({ locations: [] });
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [locationId, setLocationId] = useState("");
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "date", dir: "desc" });

  useEffect(() => {
    let active = true;
    api.get("/stock-in/belanja/options")
      .then((res) => {
        if (active) setOptions(res.data || { locations: [] });
      })
      .catch(() => {
        if (active) setError("Daftar lokasi gagal dimuat.");
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");

    api.get("/stock-in/history", {
      params: {
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        location_id: locationId || undefined,
        transaction_type: kind,
        limit: 1000,
      },
    })
      .then((res) => {
        if (active) setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (active) {
          setRows([]);
          setError(
            err?.response?.data?.detail ||
            "Riwayat gagal dimuat. Periksa akses atau koneksi."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [dateFrom, dateTo, locationId, kind]);

  const columns = [
    { key: "date", label: "Tanggal" },
    { key: "location_name", label: "Lokasi" },
    { key: "transaction_type", label: "Jenis" },
    { key: "item_name", label: "Nama Item" },
    { key: "quantity", label: "Jumlah" },
    { key: "unit", label: "Satuan" },
    { key: "status", label: "Status" },
  ];

  const sortedRows = [...rows].sort((a, b) => {
    const av = a?.[sort.key] ?? "";
    const bv = b?.[sort.key] ?? "";
    let result;
    if (sort.key === "quantity") {
      result = Number(av) - Number(bv);
    } else {
      result = String(av).localeCompare(String(bv), "id", {
        numeric: true,
        sensitivity: "base",
      });
    }
    return sort.dir === "asc" ? result : -result;
  });

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      dir: current.key === key && current.dir === "asc" ? "desc" : "asc",
    }));
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? String(value)
      : d.toLocaleString("id-ID", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
  };

  const typeLabel = (value) => ({
    belanja: "Belanja",
    transfer: "Transfer",
    manual: "Manual",
  }[value] || value || "-");

  return (
    <div className="space-y-4 rounded-lg border bg-white p-4 md:p-6">
      <div>
        <h2 className="text-lg font-semibold">Riwayat Stock In</h2>
        <p className="mt-1 text-sm text-slate-500">
          Riwayat penerimaan stok yang dapat diverifikasi.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1 text-sm">
          <span className="font-medium">Tanggal dari</span>
          <input
            type="date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full rounded-md border px-3 py-2"
          />
        </label>

        <label className="space-y-1 text-sm">
          <span className="font-medium">Tanggal sampai</span>
          <input
            type="date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full rounded-md border px-3 py-2"
          />
        </label>

        <label className="space-y-1 text-sm">
          <span className="font-medium">Lokasi</span>
          <select
            value={locationId}
            onChange={(e) => setLocationId(e.target.value)}
            className="w-full rounded-md border bg-white px-3 py-2"
          >
            <option value="">Semua lokasi</option>
            {(options.locations || []).map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name} {loc.type === "warehouse" ? "(Gudang)" : "(Outlet)"}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1 text-sm">
          <span className="font-medium">Jenis transaksi</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            className="w-full rounded-md border bg-white px-3 py-2"
          >
            <option value="all">Semua jenis</option>
            <option value="belanja">Belanja</option>
            <option value="transfer">Transfer</option>
            <option value="manual">Manual</option>
          </select>
        </label>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="text-sm text-slate-500">
        {loading ? "Memuat riwayat..." : `${sortedRows.length} baris item`}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full min-w-[850px] border-collapse text-left text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((col) => (
                <th key={col.key} className="whitespace-nowrap border-b px-3 py-3 font-semibold">
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className="inline-flex items-center gap-1 hover:text-blue-700"
                    aria-label={`Urutkan berdasarkan ${col.label}`}
                  >
                    {col.label}
                    <span className="text-xs text-slate-400">
                      {sort.key === col.key ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
                    </span>
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && sortedRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-10 text-center text-slate-500">
                  Belum ada riwayat untuk filter ini.
                </td>
              </tr>
            )}
            {sortedRows.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="whitespace-nowrap border-b px-3 py-3">{formatDate(row.date)}</td>
                <td className="border-b px-3 py-3">{row.location_name || "-"}</td>
                <td className="border-b px-3 py-3">{typeLabel(row.transaction_type)}</td>
                <td className="border-b px-3 py-3">{row.item_name || "-"}</td>
                <td className="border-b px-3 py-3 text-right tabular-nums">
                  {Number(row.quantity || 0).toLocaleString("id-ID", { maximumFractionDigits: 4 })}
                </td>
                <td className="border-b px-3 py-3">{row.unit || "-"}</td>
                <td className="border-b px-3 py-3">{row.status || "UNKNOWN"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const validTabs = ["belanja", "transfer", "manual", "riwayat"];

export default function StockInPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    validTabs.includes(requestedTab) ? requestedTab : "belanja"
  );

  useEffect(() => {
    const requested = searchParams.get("tab");
    if (validTabs.includes(requested)) setActiveTab(requested);
  }, [searchParams]);

  const changeStockInTab = (value) => {
    if (!validTabs.includes(value)) return;
    setActiveTab(value);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set("tab", value);
      return next;
    }, { replace: true });
  };

  return (
    <div data-workspace-redundant-header><div className="space-y-4">
      <h1 className="text-2xl font-semibold">Stock In</h1></div>
      <Tabs value={activeTab} onValueChange={changeStockInTab} className="w-full" data-layered-navigation="stock-in">
        <TabsList data-global-nav-layer="2" aria-label="Navigasi Stock In">
          <TabsTrigger value="belanja">Belanja</TabsTrigger>
          <TabsTrigger value="transfer">Transfer</TabsTrigger>
          <TabsTrigger value="manual">Manual</TabsTrigger>
          <TabsTrigger value="riwayat">Riwayat</TabsTrigger>
        </TabsList>
        <TabsContent value="belanja">
          <BelanjaTab />
        </TabsContent>
        <TabsContent value="transfer">
          <TransferTab />
        </TabsContent>
        <TabsContent value="manual">
          <ManualTab />
        </TabsContent>
        <TabsContent value="riwayat">
          <RiwayatTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
